import { memo, type MouseEvent, useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import LanguageIcon from '@mui/icons-material/Language';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Logout from '@mui/icons-material/Logout';
import { useMsal } from '@azure/msal-react';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import Grid from '@mui/material/Grid';
import EditIcon from '@mui/icons-material/Edit';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useTranslation } from 'react-i18next';

import EditUserAvatar from 'hooks/FileStorage/useChangeUserAvatar';
import { useGlobalContext } from 'context/GlobalContext';
import UseServiceSendFormUser from 'pages/Admin/Users/serviceUser/serviceSendFormUser';

import SelectLanguagePopup from '../SelectLanguagePopup';

function NavMenu() {
  const { dataUserActive: userData } = UseServiceSendFormUser({});

  const { showError } = useGlobalContext();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const { t } = useTranslation('global');

  const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);

  const [newImage, setNewImage] = useState('');

  const open = Boolean(anchorEl);
  const openLang = Boolean(anchorElLang);

  const { instance } = useMsal();

  const handleClick = (event: Event) => {
    setAnchorEl(event.currentTarget as Element);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    if (localStorage.getItem('onPage') === 'movements') {
      const modalToExitPage = document.getElementById('showModalFromLogout');
      modalToExitPage.style.display = 'flex';
      return;
    }
    await instance.logout();
  };

  const handleLangClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorElLang(event.currentTarget);
  };

  const handleLangClose = () => {
    setAnchorElLang(null);
  };

  const stringAvatar = (name?: string) => ({
    children: name ? `${name.split(' ')[0][0]}${name.split(' ')[1][0]}` : ''
  });

  const HashImage = new Date().getTime();

  const edituserImage = EditUserAvatar();

  const handleFileLoadEnd = useCallback(
    (file: File) => () => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onloadend = () => {
        const base64String = reader.result;
        const newBase64 = base64String
          .toString()
          .replace(`data:${file.type};base64,`, '');

        const dataFormated = {
          fileName: file.name,
          description: file.name,
          fileContent: newBase64
        };

        edituserImage?.mutate(dataFormated, {
          onSuccess: (data) => {
            setNewImage(`${data?.data?.data?.blobStorageUrlPath}?${HashImage}`);
          }
        });
      };
    },
    [HashImage, edituserImage]
  );

  const handleFileInputChange = useCallback(
    (e) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      e.preventDefault();

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const file = e?.target?.files[0] as File;

      if (file) {
        if (file.size <= 2000000) {
          return handleFileLoadEnd(file)(); // <- no le basto con el file :(, tuve invocarla dos veces;
        }
        return showError(t('errorMessages.maxSizeExceeded'));
      }
      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleFileLoadEnd]
  );

  const openFileSelector = useCallback(() => {
    const input = document.getElementById('upload-avatar-pic');

    if (input) {
      input.click();
      input.addEventListener('change', handleFileInputChange);
    }
  }, [handleFileInputChange]);

  useEffect(() => {
    if (userData?.blobStorageUrlPath) {
      setNewImage(userData?.blobStorageUrlPath);
    }
  }, [userData]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title={t('general.accountConfiguration')}>
          <IconButton
            aria-controls={open ? 'account-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
          >
            <Avatar
              alt={userData?.fullName}
              {...stringAvatar(userData?.fullName)}
              src={`${newImage}`}
              sx={{ width: 32, height: 32 }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        id="account-menu"
        onClose={handleClose}
        open={open}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            },
            width: '300px',
            height: '300px',
            top: '53px',
            left: '1608px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Grid>
          <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            {/* IMAGEN */}
            <Box
              display="flex"
              justifyContent="center"
              onClick={openFileSelector}
              sx={{
                width: '100px',
                ':hover .edit-avatar': {
                  opacity: 1,
                  cursor: 'pointer'
                }
              }}
            >
              <Avatar
                alt={userData?.fullName}
                id="avatar-user"
                src={`${newImage}`}
                sx={{
                  width: '100px !important',
                  height: '100px !important',
                  marginLeft: '0 !important',
                  marginRight: '0 !important',
                  ':hover': {
                    cursor: 'pointer',
                    opacity: 0.5
                  }
                }}
              />
              <EditIcon
                className="edit-avatar"
                sx={{
                  position: 'absolute',
                  top: '46px',
                  left: '133px',
                  opacity: 0
                }}
              />
              <input
                accept="image/*"
                id="upload-avatar-pic"
                type="file"
                hidden
              />
            </Box>
          </Box>

          {/* DATOS */}
          <Box
            alignItems="center"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box paddingLeft="10px" paddingRight="10px" paddingTop="10px">
              <Typography variant="body1">{userData?.fullName}</Typography>
            </Box>

            <Box paddingBottom="10px" paddingLeft="10px" paddingRight="10px">
              <Typography
                sx={{ wordBreak: 'break-all', textAlign: 'center' }}
                variant="body1"
              >
                {userData?.email}
              </Typography>
            </Box>
            <Divider
              sx={{
                width: '88%'
              }}
            />
            <Grid alignItems="center" flexDirection="column" container>
              <Grid>
                <Typography>{userData?.userName}</Typography>
              </Grid>
              <Grid>
                <Typography>{userData?.systemRoleName}</Typography>
              </Grid>
            </Grid>
          </Box>
          {/* BOTONES DE ACCION */}
          <Box display="flex">
            <MenuItem
              onClick={handleLogout}
              sx={{
                width: '100%',
                justifyContent: 'center'
              }}
            >
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              {t('general.signOut')}
            </MenuItem>
            <MenuItem
              onClick={handleLangClick}
              sx={{
                width: '100%',
                justifyContent: 'right',
                alignItems: 'center'
              }}
            >
              <Tooltip title={t('general.selectLanguage')}>
                <Box>
                  <LanguageIcon />
                  {openLang ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                </Box>
              </Tooltip>
            </MenuItem>
            <Popover
              anchorEl={anchorElLang}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              onClose={handleLangClose}
              open={openLang}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
            >
              <SelectLanguagePopup />
            </Popover>
          </Box>
        </Grid>
      </Menu>
    </>
  );
}

export default memo(NavMenu);
